import { parseIsoDate } from './dateUtils';

/**
 * Color indication based on deadline proximity
 */
export type DeadlineColor = 'green' | 'yellow' | 'red';

/**
 * Calculate the color indication for a ticket based on its deadline.
 *
 * Color logic:
 * - Green: All days before the deadline
 * - Yellow: On deadline day + 1 day after (deadline day 0 to +1)
 * - Red: 2+ days after deadline (deadline day +2 onwards)
 *
 * @example
 * // Deadline is 2026-07-15, today is 2026-07-23
 * calculateDeadlineColor('2026-07-15', new Date('2026-07-14')) // 'green' (before deadline)
 * calculateDeadlineColor('2026-07-15', new Date('2026-07-15')) // 'yellow' (on deadline)
 * calculateDeadlineColor('2026-07-15', new Date('2026-07-16')) // 'yellow' (1 day after)
 * calculateDeadlineColor('2026-07-15', new Date('2026-07-17')) // 'red' (2 days after)
 * calculateDeadlineColor('2026-07-15', new Date('2026-07-23')) // 'red' (8 days after)
 * calculateDeadlineColor(null) // null (no deadline)
 */
export function calculateDeadlineColor(
  deadline: string | null | undefined,
  currentDate: Date = new Date(),
): DeadlineColor | null {
  if (!deadline) return null;

  const deadlineDate = parseIsoDate(deadline);
  if (!deadlineDate) return null;

  // Normalize dates to start of day (ignore time component)
  const deadlineDay = new Date(
    deadlineDate.getFullYear(),
    deadlineDate.getMonth(),
    deadlineDate.getDate(),
  );
  const currentDay = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    currentDate.getDate(),
  );

  // Calculate difference in days
  const diffMs = currentDay.getTime() - deadlineDay.getTime();
  const daysDiff = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Green: Before deadline (negative diff)
  if (daysDiff < 0) return 'green';

  // Yellow: On deadline day (0) or 1 day after (1)
  if (daysDiff <= 1) return 'yellow';

  // Red: 2+ days after deadline
  return 'red';
}
